import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { REQUEST } from '../../../express.tokens';
import { environment } from '../../../environments/environment';
import { PlatformService } from '../services/platform.service';
import { ignoreHosts } from '../../../config/ignore.hosts';

@Injectable({
  providedIn: 'root',
})
export class HostRequestsInterceptor implements HttpInterceptor {
  private _platform = inject(PlatformService);
  private _injector = inject(Injector);


  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const appUrl = `https://${this._getAppHost()}`;
    const targetAcceptHeaderV1 = 'application/vnd.s.v1+json';
    const targetAcceptHeaderV2 = 'application/vnd.s.v2+json';

    const finalUrl = req.params.has('crossdomain') ? req.url : this.overrideUrl(req.url, appUrl);

    if (this._platform.isBrowser) {
      req = req.clone({ url: finalUrl });
    } else {
      if (this._isRequestToCms(finalUrl)) {
        const serverHeaders = this._injector.get(REQUEST).headers as Record<string, any>;
        req = req.clone({
          url: finalUrl.replace('https://', 'http://'),
          setHeaders: serverHeaders,
        });
      }
    }

    if (this._isRequestToSS(finalUrl)) {
      req = req.clone({
        setHeaders: {
          url: finalUrl,
          Accept: this._shouldUseV2(finalUrl) ? targetAcceptHeaderV2 : targetAcceptHeaderV1,
        },
      });
    }

    return next.handle(req);
  }

  private _shouldUseV2(pathname: string): boolean {
    const p = pathname.toLowerCase();
    return (
      p.includes('/api/games_filter') ||
      p.includes('/api/games/random') ||
      p.includes('/api/player/favorite_games') ||
      p.includes('/api/stats/winners/')
    );
  }

  /**
   * Get domain name
   */
  public getDomainName() {
    return this._getAppHost();
  }

  /**
   * Change provided url to depending on host url
   *
   * @param url
   * @param appUrl
   * @private
   */
  public overrideUrl(url: string, appUrl: string = `https://${this._getAppHost()}`): string {
    switch (true) {
      case this._isIgnoredHost(appUrl):
        return url;
      case this._isRequestToCms(url):
        return this._overrideCmsHost(url, appUrl, environment.cms_host);
      case this._isRequestToSS(url):
        return this._overrideSSHost(url, appUrl, environment.ss_host);
      case this._isRequestToMlh(url):
        return this._overrideMlhHost(url, appUrl, environment.mlh_host);
      case this._isErrorRequest(url):
        return this._overrideErrorHost(url);
      case this._isRequestToAssets(url):
        return this._overrideAssetsHost(url);
      default:
        return url;
    }
  }

  /**
   * Returns hostname on which app running
   *
   * @private
   */
  private _getAppHost(): string | undefined {
    return this._platform.isBrowser
      ? window.location.host
      : this._injector.get(REQUEST).headers.host;
  }

  /**
   * Returns true if request url is assets url
   *
   * @param url
   * @private
   */
  private _isRequestToAssets(url: string): boolean {
    return url.startsWith(environment.assets_host);
  }

  /**
   * Override request to SS
   *
   * @private
   */
  private _overrideSSHost(url: string, host: string, ssHost: string): string {
    return url.replace(ssHost, host).replace(/https:\/\/stage.|https:\/\/www.|https:\/\/dev./g, 'https://');
  }

  /**
   * Override request to cms
   *
   * @private
   */
  private _overrideCmsHost(url: string, host: string, cmsHost: string): string {
    return url.replace(cmsHost, host).replace('https://www.', 'https://api.');
  }

  /**
   * Returns true if request url is CMS url
   *
   * @param url
   * @private
   */
  private _isRequestToCms(url: string): boolean {
    return (url.includes('dev-api.') || url.includes('stage-api.') || url.includes('api.'))
  }

  /**
   * Returns true if request url is SS url
   *
   * @param url
   * @private
   */
  private _isRequestToSS(url: string): boolean {
    return (!url.includes('dev-api.') && !url.includes('stage-api.') && !url.includes('api.'))
  }

  /**
   * Returns true if url should not be override
   *
   * @param host
   * @private
   */
  private _isIgnoredHost(host: string): boolean {
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return true;
    }
    return ignoreHosts.some(item => host.includes(item));
  }

  /**
   * Returns true if request url is mlh url
   *
   * @param url
   * @private
   */
  private _isRequestToMlh(url: string): boolean {
    return url.startsWith(environment.mlh_host);
  }

  /**
   * Returns true if request url is mlh url
   *
   * @param url
   * @private
   */
  private _isErrorRequest(url: string): boolean {
    return url.startsWith(environment.error_host + '/error/handle');
  }

  /**
   * Override request to mlh
   *
   * @private
   */
  private _overrideMlhHost(url: string, host: string, mlhHost: string): string {
    return url.replace(mlhHost, host).replace('https://www.', 'https://mlh.');
  }

  /**
   * Override request to SS
   *
   * @private
   */
  private _overrideErrorHost(url: string): string {
    const hostPrefix = environment.production ? 'www.' : 'stage.';
    const hostname = this.getDomainName().replace(hostPrefix, '');
    return url.replace(/\b21bit\.com\b/g, hostname);
  }

  /**
   * Override request to assets
   *
   * @private
   */
  private _overrideAssetsHost(url: string): string {
    const hostPrefix = environment.production ? 'www.' : 'stage.';
    const hostname = this.getDomainName().replace(hostPrefix, '');
    return url.replace(/\b21bit\.com\b/g, hostname);
  }
}
